'use client';


/** Imports */
import {EMAIL_OTP_RESEND_COOLDOWN_SECONDS} from '@/modules/emailOtpPolicy';
import {useTranslations} from 'next-intl';
import {type Session} from 'next-auth';
import Dialog from '@/components/Dialog/Dialog';
import Button from '@/components/Button/Button';
import {SessionProvider} from 'next-auth/react';
import {TRPCReactProvider} from '@/trpc/client';
import {parseDurationMs} from 'ms-parse-duration';
import Loading from '@/components/Loading/Loading';
import {parseLanguageCode} from '@/i18n/localeUtils';
import styles from '@/app/[locale]/auth/[page]/page.module.css';
import {generateNewEmailOtpDirectAction, verifyEmailOtpDirectAction} from '@/actions/account';
import React, {createContext, useRef, useState, type ChangeEvent, useEffect, useCallback, useMemo} from 'react';


/** Types */
type Props = Readonly<{
    children: React.ReactNode;
    session?: Session | null;
}>;
type OTPDialogProps = Readonly<{
    reference: React.RefObject<HTMLDialogElement | null>;
    setSystemOTP: React.Dispatch<React.SetStateAction<string>>;
}>;
type ContextType = {
    requireOTP: () => Promise<string>;
};


/** Constants */
export const SecurityContext = createContext<ContextType>(null as any);


/** Component */
export function AuthProvider(props: Props) {
    const {children, session} = props;
    return (
        <SessionProvider session={session}>
            <TRPCReactProvider>
                <SecurityContextProvider>
                    {children}
                </SecurityContextProvider>
            </TRPCReactProvider>
        </SessionProvider>
    );
}

function SecurityContextProvider(props: Props) {
    const {children} = props;

    // Refs
    const otpDialogRef = useRef<HTMLDialogElement | null>(null);

    // State
    const [systemOTP, setSystemOTP] = useState('');
    const [otpResolve, setOtpResolve] = useState<((otp: string) => void) | null>(null);

    // Memos
    const requireOTP = useCallback(() => {
        return new Promise<string>((resolve) => {
            otpDialogRef.current?.showModal();
            setOtpResolve(() => resolve);
        });
    }, []);
    const contextValue = useMemo(() => ({requireOTP}), [requireOTP]);

    // Effects
    useEffect(() => {
        if (!systemOTP || !otpResolve) return;
        otpDialogRef.current?.close();
        otpResolve(systemOTP);
        setSystemOTP('');
        setOtpResolve(null);
    }, [systemOTP, otpResolve]);


    // Render
    return (
        <SecurityContext.Provider value={contextValue}>

            {/* Require OTP dialog */}
            <RequireOTPDialog
                reference={otpDialogRef}
                setSystemOTP={setSystemOTP}
            />

            {/* Content */}
            {children}
        </SecurityContext.Provider>
    );
}

/**
 * Renders a modal dialog that runs a two-step email OTP flow and delivers the obtained OTP.
 *
 * The dialog lets the user request a verification code and then enter it; on successful verification
 * the provided `setSystemOTP` callback is called with the received OTP. The component manages resend
 * countdown, loading and error states, and cleans up any timers when the dialog closes.
 *
 * @param props.reference A ref to the dialog element, used to control its visibility.
 * @param props.setSystemOTP A callback function that is called with the obtained OTP after successful verification.
 * @returns The dialog element implementing the OTP request and verification UI.
 */
function RequireOTPDialog(props: OTPDialogProps) {
    const {reference, setSystemOTP} = props;

    // Hooks
    const t = useTranslations('Auth');
    const tCommon = useTranslations('common');

    // Refs
    const timerInterval = useRef<ReturnType<typeof setInterval> | undefined | null>(null);

    // State
    const [step, setStep] = useState(0);
    const [verificationCode, setVerificationCode] = useState('');
    const [resendTimeout, setResendTimeout] = useState(0);
    const [hasError, setHasError] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    // Event handlers
    function onVerificationCodeInputChange(event: ChangeEvent<HTMLInputElement>) {
        if (!event.target.value.match(/^\d{0,6}$/)) return;
        setVerificationCode(event.target.value);
    }

    async function onVerifyIdentityButtonClick() {
        if (isLoading) return;
        if (!verificationCode) return;
        setIsLoading(true);
        const result = await verifyEmailOtpDirectAction(verificationCode);
        setIsLoading(false);
        if (!result?.success || !result?.data) {
            setHasError(true);
            return;
        }
        setSystemOTP(result.data);
        setHasError(false);
        setStep(2);
    }

    async function onSendVerificationCodeButtonClick() {
        if (isLoading) return;
        setIsLoading(true);
        setResendTimeout(EMAIL_OTP_RESEND_COOLDOWN_SECONDS);
        const result = await generateNewEmailOtpDirectAction(parseLanguageCode(tCommon('locale')));
        setIsLoading(false);
        if (!result) {
            setHasError(true);
            return;
        }
        setHasError(false);
        setStep(1);
        if (timerInterval.current) clearInterval(timerInterval.current);
        timerInterval.current = setInterval(() => {
            setResendTimeout((prev) => {
                const nextValue = prev - 1;
                if (nextValue <= 0) {
                    clearInterval(timerInterval.current!);
                    timerInterval.current = null;
                    return 0;
                }
                return nextValue;
            });
        }, parseDurationMs('1s'));
    }

    // Event handler
    function onOpen() {
        setStep(0);
        setVerificationCode('');
        setResendTimeout(0);
        setHasError(false);
        setIsLoading(false);
    }

    function onClose() {
        if (timerInterval.current) {
            clearInterval(timerInterval.current);
            timerInterval.current = null;
        }
    }


    // Render
    return (
        <Dialog
            reference={reference}
            headline={t('otpDialog.headline')}
            subHeadline={t('otpDialog.subHeadline')}
            disableCloseOnBackdrop={true}
            extraClasses={[styles.changeSecuredDataModal]}
            onOpen={onOpen} onClose={onClose}
            foot={(<>

                {/* Step 1 */}
                {(step === 0) && (
                    <div className="flex-group">
                        <Button variant="primary" onClick={onSendVerificationCodeButtonClick}>
                            {(isLoading)
                                ? (<Loading
                                    color="rgb(var(--color-text-on-primary))"
                                    cssOverride={{height: '1rem'}}
                                />)
                                : t('otpDialog.buttonStep1')}
                        </Button>
                        {hasError && (
                            <div className="error-message">
                                {t('otpDialog.error.step1')}
                            </div>
                        )}
                    </div>
                )}

                {/* Step 2 */}
                {(step === 1) && (
                    <div className="flex-group">
                        <Button variant="primary" onClick={onVerifyIdentityButtonClick}>
                            {(isLoading)
                                ? (<Loading
                                    color="rgb(var(--color-text-on-primary))"
                                    cssOverride={{height: '1rem'}}
                                />)
                                : t('otpDialog.buttonStep2')}
                        </Button>
                        {hasError && (
                            <div className="error-message">
                                {t('otpDialog.error.step2')}
                            </div>
                        )}
                    </div>
                )}
            </>)}
        >
            <div className={styles.changeSecuredDataModal}>
                <div className={styles.step}>
                    <div className={styles.box}>

                        {/* Intro */}
                        <div className={styles.intro}>
                            {t.rich(`otpDialog.introStep${step + 1}`, {
                                br: () => <br/>,
                                strong: (chunks) => <strong>{chunks}</strong>,
                            })}
                            <br/>
                            <div className={styles.small}>
                                {t(`otpDialog.introSmallStep${step + 1}`)}
                            </div>
                        </div>

                        {/* Input */}
                        {(step === 1) && (
                            <div className={styles.input}>
                                <input
                                    className="--otp"
                                    type="text"
                                    placeholder={t('otpDialog.placeholderVerificationCode')}
                                    value={verificationCode}
                                    onInput={onVerificationCodeInputChange}
                                />
                            </div>
                        )}

                        {/* Resend */}
                        {(step === 1) && (
                            <div className={styles.resend}>
                                {(resendTimeout > 0)
                                    ? t.rich('otpDialog.resendCodeWait', {
                                        seconds: resendTimeout,
                                        strong: (chunks) => <strong>{chunks}</strong>,
                                    })
                                    : t.rich('otpDialog.resendCodeAllowed', {
                                        button: (chunks) => (<>&nbsp;
                                            <Button variant="secondary" onClick={onSendVerificationCodeButtonClick}>
                                                {(isLoading)
                                                    ? (<Loading cssOverride={{height: '1rem'}}/>)
                                                    : chunks}
                                            </Button>
                                        </>),
                                    })}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Dialog>
    );
}
